<template>
  <div :id="id" class="assign-roles">
    <v-tooltip
      id="assign-roles-tooltip"
      v-model="showTooltip"
      class="assign-roles-tooltip"
      location="top"
      :disabled="canRunBulkAction"
    >
      <template #activator="{ props }">
        <div v-bind="props">
          <cs-button
            id="assign-roles-btn"
            :disabled="!canRunBulkAction"
            class="assign-roles-btn"
            :label="label"
            @click="onAssignRolesClick"
          >
          </cs-button>
        </div>
      </template>
      <span id="assign-roles-tooltip-message" class="assign-roles-tooltip-message">{{
        tooltip
      }}</span>
    </v-tooltip>

    <invalid-connection-users-warning-dialog
      id="invalid-connection-assign-roles-dialog"
      v-model="showWarningDialog"
      :connection-name="connectionName"
      table-id="invalid-connection-assign-roles-table"
      :users="selected"
      @dismiss="dismissWarningDialog"
    />

    <user-roles-select-checkbox-dialog
      id="show-users-assign-roles-dialog"
      v-model="showConfirmDialog"
      :heading="label"
      :primary-label="primaryLabel"
      checkbox-hint="Assign roles to the user"
      secondary-label="Cancel"
      @edit-roles="onConfirm"
      @cancel-roles="onCancel"
    />

    <progress-dialog
      id="assign-roles-progress-dialog"
      cancel-btn-label="Cancel"
      heading="Assigning Roles to Users"
      progress-bar-id="assign-roles-progress-bar"
      :model-value="showProgressDialog"
      :progress-percent="progressPercent"
      @progress-cancel="onProgressDialogCancel"
    />
  </div>
</template>
<script>
import { mapGetters } from 'vuex';

import BulkActionBase from '@/components/bulkActions/BulkActionBase';
import BulkActionName from '@/components/bulkActions/bulkActionName';

import InvalidConnectionUsersWarningDialog from '@/components/InvalidConnectionUsersWarningDialog';
import ProgressDialog from '@/components/ProgressDialog';
import UserRolesSelectCheckboxDialog from '@/components/UserRolesSelectCheckboxDialog';

import componentErrorHandler from '@/helpers/componentErrorHandler';

import delay from '@/helpers/delay';

export default {
  name: 'AssignRoles',

  components: {
    'invalid-connection-users-warning-dialog': InvalidConnectionUsersWarningDialog,
    'progress-dialog': ProgressDialog,
    'user-roles-select-checkbox-dialog': UserRolesSelectCheckboxDialog
  },
  extends: BulkActionBase,

  props: {
    connectionName: {
      type: String,
      required: true
    },

    selected: {
      type: Array,
      required: true
    }
  },

  emits: ['assign-roles-completed'],

  data() {
    return {
      id: this.$attrs.id,
      label: 'Assign Role(s)',
      primaryLabel: 'Assign',
      tooltip: 'Please select at least one user to perform this action.',

      showConfirmDialog: false,
      selectedRoles: [],
      showTooltip: false
    };
  },

  computed: {
    ...mapGetters({
      roles: 'organization/roles'
    }),

    hasRoles() {
      return this.roles.length > 0;
    }
  },

  async mounted() {
    await delay(1000);
  },

  methods: {
    async onAssignRolesClick() {
      if (!this.hasRoles) {
        componentErrorHandler(
          this,
          undefined,
          'Please contact Complispace Admin to setup the roles for the organization.',
          true
        );
        return;
      }

      if (this.containsUsersWithMismatchedConnections) {
        await this.checkForUsersWithMismatchedConnections();
        return;
      }

      this.showConfirmDialog = true;

      await this.$nextTick();
      this.showWarningAlert(
        'Are you sure you want to assign roles. This action cannot be undone.',
        true
      );
    },

    async onConfirm(selectedRoles) {
      if (!selectedRoles.length) {
        await this.$nextTick();
        componentErrorHandler(this, undefined, 'You need to select at least one role.', true);
        return;
      }

      this.selectedRoles = selectedRoles;
      this.showConfirmDialog = false;

      this.initBulkActionProgress(BulkActionName.ASSIGN_ROLES_TO_USERS);

      await this.selected.reduce(this.runActionOnUsers, undefined);

      this.toggleProgressDialog(false);
      this.setProgressPercent(0);

      const failedUsersLen = this.failedBulkActionUsers.length;
      const message = this.getBulkActionMessage();

      this.showBulkActionAlert(message, failedUsersLen);

      this.selectedRoles = [];
      this.clearBulkActionState();

      this.$emit('assign-roles-completed', {
        failedUsersLen,
        successfulUsersLen: this.successfulBulkActionUsers.length
      });
    },

    onCancel() {
      this.showConfirmDialog = false;
    }
  }
};
</script>
<style scoped>
@media screen and (max-width: 1264px) {
  #assign-roles-btn {
    min-width: 100%;
  }
}
</style>
